import {z} from 'zod';
import {organizationIdSchema, userIdSchema, type OrganizationId} from './id';
import {localeSchema} from './locale';
import {assignableRoleSchema, roleSchema} from './role';
import {isAnonymisedEmail} from './user-deletion';

/** Emails are stored and compared lowercase; Cognito usernames are case-insensitive. */
export const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(320));

/** The one name rule, so every schema that accepts a name agrees on what a name is. */
export const userNameSchema = z.string().trim().min(1).max(200);

export const USER_STATUSES = ['ACTIVE', 'DISABLED'] as const;

export type UserStatus = (typeof USER_STATUSES)[number];

export const userStatusSchema = z.enum(USER_STATUSES);

/**
 * A platform admin has no organization; every other role must have one.
 * Enforced here, by a CHECK constraint in the database and by the authorization policy.
 */
const organizationMatchesRole = <T extends {role: z.infer<typeof roleSchema>; organizationId: OrganizationId | null}>(
  value: T,
  ctx: z.RefinementCtx,
): void => {
  const shouldHaveOrganization = value.role !== 'PLATFORM_ADMIN';
  if (shouldHaveOrganization && value.organizationId === null) {
    ctx.addIssue({code: 'custom', path: ['organizationId'], message: 'ORGANIZATION_REQUIRED_FOR_ROLE'});
  }
  if (!shouldHaveOrganization && value.organizationId !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['organizationId'],
      message: 'PLATFORM_ADMIN_HAS_NO_ORGANIZATION',
    });
  }
};

export const userSchema = z
  .object({
    id: userIdSchema,
    email: z.email().max(320),
    name: userNameSchema,
    role: roleSchema,
    organizationId: organizationIdSchema.nullable(),
    status: userStatusSchema,
    locale: localeSchema,
    /**
     * Set when the account was deleted. A deleted user keeps their row and their id, so
     * every reference to them still resolves, but their email and name have been replaced
     * by `anonymousIdentityOf` — see `user-deletion.ts` and ADR 0018.
     */
    deletedAt: z.coerce.date().nullable(),
    /** Spared by an organization's scheduled deletion. Only a platform admin may set it. */
    exemptFromScheduledDeletion: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .superRefine(organizationMatchesRole);

export type User = z.infer<typeof userSchema>;

/**
 * Body of `POST /api/users`. `organizationId` is optional: an organization admin
 * always creates users inside their own organization, so only a platform admin
 * has to name one. See `resolveTargetOrganizationId`.
 *
 * `locale` is the language the invitation is written in. The invitee has never signed in
 * and so has no preference of their own yet; the administrator creating the account is
 * the only one who knows which language to greet them in. They can change it afterwards.
 */
export const createUserSchema = z.object({
  // The anonymous namespace belongs to deleted rows. See `isAnonymisedEmail`.
  email: emailSchema.refine(value => !isAnonymisedEmail(value), {message: 'EMAIL_RESERVED'}),
  name: userNameSchema,
  role: assignableRoleSchema,
  organizationId: organizationIdSchema.optional(),
  locale: localeSchema.optional(),
});

export type CreateUser = z.infer<typeof createUserSchema>;

/** Email is the Cognito username and therefore immutable. */
export const updateUserSchema = z
  .object({
    name: userNameSchema,
    role: assignableRoleSchema,
    status: userStatusSchema,
    /** Platform admins only, and `canUpdateUser` is where that is refused. */
    exemptFromScheduledDeletion: z.boolean(),
  })
  .partial()
  .refine(value => Object.keys(value).length > 0, {message: 'NO_FIELDS_TO_UPDATE'});

export type UpdateUser = z.infer<typeof updateUserSchema>;

/**
 * Body of `PATCH /api/me` — a user may rename themselves and choose the language they
 * are written to in. Both optional, because the language switcher saves on its own
 * without dragging the name field along with it.
 */
export const updateProfileSchema = z
  .object({
    name: userNameSchema,
    locale: localeSchema,
  })
  .partial()
  .refine(value => Object.keys(value).length > 0, {message: 'NO_FIELDS_TO_UPDATE'});

export type UpdateProfile = z.infer<typeof updateProfileSchema>;

/**
 * Self-service password reset. The response never says whether the address was known —
 * see docs/adr/0009-email-mfa-and-branded-cognito-mail.md.
 */
export const passwordResetRequestSchema = z.object({
  email: emailSchema,
});

export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

/**
 * The new password is only checked for length here. The user pool owns the real policy,
 * and duplicating it would mean two definitions that can disagree; Cognito's complaint
 * is passed back to the user instead.
 */
export const passwordResetConfirmSchema = z.object({
  token: z.string().trim().min(1).max(200),
  password: z.string().min(12).max(256),
});

export type PasswordResetConfirm = z.infer<typeof passwordResetConfirmSchema>;
